import { ComponentPropsWithoutRef, useCallback, useRef } from "react";
import { flushSync } from "react-dom";
import { flexRender, Table as TanstackTable } from "@tanstack/react-table";

import IconArrowUpward from "../../../../../../public/assets/icons/material/round/arrow_upward.svg";
import IconArrowDownward from "../../../../../../public/assets/icons/material/round/arrow_downward.svg";

import { TableHeader } from "../../base/TableHeader";
import { DataTableHeaderCell } from "./cells/DataTableHeaderCell";
import { Flex, Icon } from "../../../../../components";
import { getCommonPinningClasses } from "../../internal/getCommonPinningClasses";
import { getCommonPinningStyles } from "../../internal/getCommonPinningStyles";
import { ResizeHandle } from "../../internal/ResizeHandle";

import styles from "./DataTableHeader.module.scss";
import { useDTHoverDispatchContext } from "./context/hover/useDTHoverDispatchContext";
import { useColumnOrder } from "./useColumnOrder";
import {
  INTERNAL_ROW_ACTION_PRIMARY_COLUMN_ID,
  INTERNAL_ROW_ACTION_SECONDARY_COLUMN_ID,
} from "./constants";

type HeaderContentAlign = "flex-start" | "center" | "flex-end";

function getHeaderContentAlign(
  align?: "start" | "center" | "end",
): HeaderContentAlign {
  if (align === "center") {
    return "center";
  }

  if (align === "end") {
    return "flex-end";
  }

  return "flex-start";
}

/**
 * Props for the DataTableHeader component
 * @extends TanstackTable<T>
 * @extends ComponentPropsWithoutRef<"div">
 */
type DataTableHeaderProps<T> = {
  table: TanstackTable<T>;
} & ComponentPropsWithoutRef<"div">;

/**
 * Data table header container
 * @param props - The props for the DataTableHeader component
 * @param props.table - The table instance from @tanstack/react-table to render
 */
export function DataTableHeader<T>({ table }: DataTableHeaderProps<T>) {
  const dispatch = useDTHoverDispatchContext();

  const handleHoverColumn = useCallback(
    (columnId: string) => {
      dispatch({
        type: "HOVER_START",
        payload: { columnId },
      });
    },
    [dispatch],
  );

  const handleHoverColumnLeave = useCallback(
    (columnId: string) => {
      dispatch({
        type: "HOVER_END",
        payload: { columnId },
      });
    },
    [dispatch],
  );

  // Store refs to header cells for measuring actual DOM width during resize
  const cellRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Create a resize start handler that initializes columnSizing with the actual DOM width
  // Uses flushSync to ensure the DOM is updated with the new width before TanStack measures it
  const createResizeStartHandler = useCallback(
    (columnId: string) => () => {
      const cellElement = cellRefs.current.get(columnId);
      if (cellElement) {
        const actualWidth = cellElement.getBoundingClientRect().width;
        flushSync(() => {
          table.setColumnSizing((prev) => ({
            ...prev,
            [columnId]: actualWidth,
          }));
        });
      }
    },
    [table],
  );

  const handleResizeEnd = useCallback(
    (columnId: string) => {
      dispatch({
        type: "RESIZE_END",
        payload: { columnId },
      });
    },
    [dispatch],
  );

  const { columnOrder } = useColumnOrder({ table });

  return (
    <TableHeader type="data-table">
      {table.getHeaderGroups().map((headerGroup, index) => (
        <div
          key={headerGroup.id}
          className={styles["data-table-header-row"]}
          role="row"
          aria-rowindex={index + 1}
        >
          {headerGroup.headers.map((header) => {
            const isLastInParentGroup = header.column.parent
              ? header.column.parent.getLeafColumns().slice(-1)[0] ===
                header.column
              : false;

            const isInternalSelectColumn =
              header.column.id === INTERNAL_ROW_ACTION_SECONDARY_COLUMN_ID;
            const isInternalExpandColumn =
              header.column.id === INTERNAL_ROW_ACTION_PRIMARY_COLUMN_ID;

            // Use grid-column span for grouped columns
            const gridColumn =
              header.colSpan > 1 ? `span ${header.colSpan}` : undefined;

            const headerContentAlign = getHeaderContentAlign(
              header.column.columnDef.meta?.headerAlign,
            );
            const isGroupHeader = header.subHeaders.length > 0;
            const hasGroupDivider =
              isGroupHeader ||
              Boolean(header.column.parent && isLastInParentGroup);
            const isHeaderActionDisabled =
              header.column.columnDef.meta?.disableHeaderAction === true;
            const headerCellInteractive =
              !isGroupHeader &&
              (header.column.getCanSort() ||
                ((isInternalExpandColumn || isInternalSelectColumn) &&
                  !isHeaderActionDisabled));

            return (
              <DataTableHeaderCell
                header={header.getContext()}
                key={header.id}
                ref={(el: HTMLDivElement | null) => {
                  if (el) {
                    cellRefs.current.set(header.column.id, el);
                  } else {
                    cellRefs.current.delete(header.column.id);
                  }
                }}
                className={getCommonPinningClasses(header.column)}
                style={{
                  gridColumn,
                  ...getCommonPinningStyles(header.column),
                }}
                aria-colspan={header.colSpan}
                isGroup={hasGroupDivider}
                isSortable={header.column.getCanSort()}
                isInteractiveHeaderCell={headerCellInteractive}
                isSelectCell={isInternalSelectColumn}
                isExpandCell={isInternalExpandColumn}
                onMouseEnter={
                  headerCellInteractive
                    ? () => handleHoverColumn(header.column.id)
                    : undefined
                }
                onMouseLeave={
                  headerCellInteractive
                    ? () => handleHoverColumnLeave(header.column.id)
                    : undefined
                }
                cellPosition={{
                  rowIndex: index,
                  columnIndex: columnOrder.indexOf(header.column.getIndex()),
                }}
              >
                {header.isPlaceholder ? null : (
                  <Flex
                    direction="row"
                    alignItems="center"
                    gap={2}
                    style={{
                      width: "100%",
                    }}
                  >
                    <Flex
                      direction="row"
                      alignItems="center"
                      justifyContent={headerContentAlign}
                      gap={2}
                      grow={1}
                      style={{
                        minWidth: 0,
                        width: "100%",
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                      {header.column.getIsSorted() ? (
                        <span
                          style={{
                            alignItems: "center",
                            display: "flex",
                            flexShrink: 0,
                          }}
                        >
                          {{
                            asc: <Icon svg={IconArrowUpward} size="small" />,
                            desc: <Icon svg={IconArrowDownward} size="small" />,
                          }[header.column.getIsSorted() as string] ?? null}
                        </span>
                      ) : null}
                    </Flex>
                    {header.column.columnDef.enableResizing && (
                      <ResizeHandle
                        header={header}
                        onResizeStart={createResizeStartHandler(
                          header.column.id,
                        )}
                        onResizeEnd={() => handleResizeEnd(header.column.id)}
                      />
                    )}
                  </Flex>
                )}
              </DataTableHeaderCell>
            );
          })}
        </div>
      ))}
    </TableHeader>
  );
}
